import { useEffect, useState } from 'react';
import { areasService, type ForemanArea as AreasForemanArea } from '../services/areasService';
import type { ForemanArea } from '../types/ForemanArea';

/**
 * Reshape `areasService.getForemanAreas()` rows into the `ForemanArea` shape the
 * task modals expect. They only read `id`, `name`, `serviceAreaId` and
 * `contractAreas`, but `serviceArea` is carried through so the nested selects
 * keep working.
 *
 * The same conversion is inlined in AllTasksPage, ProjectDetailsPage and
 * ProjectWorksPage; those callers can be migrated onto this helper separately.
 */
export function toTaskModalForemanAreas(areas: AreasForemanArea[]): ForemanArea[] {
  return areas.map((area) => ({
    id: area.id,
    name: area.name,
    serviceAreaId: area.serviceArea?.id ?? area.serviceAreaId,
    serviceArea: area.serviceArea
      ? { ...area.serviceArea, foremanAreas: [], activities: [] }
      : undefined,
    contractAreas: area.contractAreas ?? [],
  })) as unknown as ForemanArea[];
}

export interface UseTaskModalForemanAreasResult {
  foremanAreas: ForemanArea[];
  loading: boolean;
  error: string | null;
}

/** Loads the foreman areas a task modal needs, once per mount. */
export function useTaskModalForemanAreas(enabled: boolean = true): UseTaskModalForemanAreasResult {
  const [foremanAreas, setForemanAreas] = useState<ForemanArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const areas = await areasService.getForemanAreas();
        if (cancelled) return;
        setForemanAreas(toTaskModalForemanAreas(areas));
      } catch (err) {
        console.error('Failed to load foreman areas for task modal', err);
        if (!cancelled) setError('Failed to load foreman areas');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { foremanAreas, loading, error };
}
